/**
 * P37 — Edit credentials modal. Used by /admin/odm-credentials.
 *
 * Only fields the user TYPES are sent to the backend, so existing
 * password/SSH key remain untouched if those textareas stay empty.
 */
import React, { useState } from 'react';
import { Eye, EyeSlash, X } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const EditCredentialModal = ({ cred, onCancel, onSave }) => {
  const [form, setForm] = useState({
    host: cred.host || '',
    port: cred.port || 22,
    username: cred.username || '',
    inbound_dir: cred.inbound_dir || '',
    outbound_dir: cred.outbound_dir || '',
    password: '',         // user must re-enter to change
    ssh_private_key: '',  // user must paste to change
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    // Send only fields that changed or were filled (password+key cleared on blank)
    const body = {
      host: form.host !== cred.host ? form.host : undefined,
      port: form.port !== cred.port ? Number(form.port) : undefined,
      username: form.username !== cred.username ? form.username : undefined,
      inbound_dir: form.inbound_dir !== cred.inbound_dir ? form.inbound_dir : undefined,
      outbound_dir: form.outbound_dir !== cred.outbound_dir ? form.outbound_dir : undefined,
      password: form.password ? form.password : undefined,
      ssh_private_key: form.ssh_private_key ? form.ssh_private_key : undefined,
    };
    await onSave(cred.environment, body);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="edit-cred-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-slate-900">
            Edit {cred.environment === 'prod' ? 'Production' : 'CERT'} credentials
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-700"
            data-testid="cred-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="cred-host">Host</Label>
              <Input
                id="cred-host"
                value={form.host}
                onChange={(e) => set('host', e.target.value)}
                placeholder="mft.oh.healthinteractive.net"
                data-testid="cred-input-host"
              />
            </div>
            <div>
              <Label htmlFor="cred-port">Port</Label>
              <Input
                id="cred-port"
                type="number"
                value={form.port}
                onChange={(e) => set('port', e.target.value)}
                data-testid="cred-input-port"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="cred-username">Username</Label>
            <Input
              id="cred-username"
              value={form.username}
              onChange={(e) => set('username', e.target.value)}
              placeholder="MCD-0022168EDISFTPP"
              data-testid="cred-input-username"
            />
          </div>

          <div>
            <Label htmlFor="cred-password">
              Password
              <span className="ml-2 text-xs text-slate-500 font-normal">
                Leave blank to keep existing
              </span>
            </Label>
            <div className="relative">
              <Input
                id="cred-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder={cred.password_masked || '— not set —'}
                className="pr-10 font-mono"
                data-testid="cred-input-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                onClick={() => setShowPassword((s) => !s)}
                data-testid="cred-toggle-password"
              >
                {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="cred-ssh">
              SSH Private Key (PEM)
              <span className="ml-2 text-xs text-slate-500 font-normal">
                Leave blank to keep existing
              </span>
            </Label>
            <Textarea
              id="cred-ssh"
              value={form.ssh_private_key}
              onChange={(e) => set('ssh_private_key', e.target.value)}
              placeholder="-----BEGIN RSA PRIVATE KEY-----..."
              rows={6}
              className="font-mono text-xs"
              data-testid="cred-input-ssh-key"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cred-inbound">Inbound directory</Label>
              <Input
                id="cred-inbound"
                value={form.inbound_dir}
                onChange={(e) => set('inbound_dir', e.target.value)}
                className="font-mono text-xs"
                data-testid="cred-input-inbound"
              />
            </div>
            <div>
              <Label htmlFor="cred-outbound">Outbound directory</Label>
              <Input
                id="cred-outbound"
                value={form.outbound_dir}
                onChange={(e) => set('outbound_dir', e.target.value)}
                className="font-mono text-xs"
                data-testid="cred-input-outbound"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="cred-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-indigo-900 hover:bg-indigo-800 text-white"
            data-testid="cred-modal-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditCredentialModal;
